import React, { useState, useEffect, useContext } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import { useGet, usePut } from '../../../sharedModules/hooks/useQuery';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import axios from '../../../sharedModules/utils/axios.utils';

interface EmailTemplateResponse {
  result: { subject?: string; body?: string };
}

const EMAIL_PREVIEW_STYLES = `
  @font-face { font-family: 'AlexBrush'; src: url('/assets/fonts/AlexBrush-Regular.ttf') format('truetype'); font-weight: normal; font-style: normal; }
  body { font-family: Arial, sans-serif; margin: 0; padding: 0; background-color: #f4f4f4; }
  .wrapper { max-width: 600px; margin: 0 auto; background-color: #ffffff; }
  .signature { font-family: 'AlexBrush', cursive; font-size: 28px; color: #111111; }
  .header { background-color: #ffffff; padding: 12px 24px; text-align: center; border-bottom: 1px solid #e0e0e0; }
  .header img { max-width: 100%; height: auto; max-height: 48px; display: block; margin: 0 auto; }
  .body { padding: 28px; color: #333333; font-size: 15px; line-height: 1.6; }
  .footer { background-color: #a21c37; padding: 12px 24px; text-align: center; font-size: 12px; color: #fff; border-top: 1px solid #e0e0e0; }
  .footer a { color: #fff; text-decoration: none; }
`;

const buildPreviewHtml = (body: string): string => `
<!DOCTYPE html>
<html>
  <head><meta charset="UTF-8"><style>${EMAIL_PREVIEW_STYLES}</style></head>
  <body>
    <div class="wrapper">
      <div class="header">
        <img src="/tudi-mechanical-systems-logo.png" alt="TUDI Mechanical Systems" />
      </div>
      <div class="body">${body}</div>
      <div class="footer">
        <p>TUDI &bull; <a href="mailto:info@tudi.com">info@tudi.com</a></p>
      </div>
    </div>
  </body>
</html>
`;

const QUILL_MODULES = {
  toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
};

const TEMPLATE_VARIABLES = [
  { variable: '{{customer_first_name}}', description: "Customer's first name" },
  { variable: '{{customer_last_name}}', description: "Customer's last name" },
  { variable: '{{technician_name}}', description: "Technician's name" },
  { variable: '{{technician_signature}}', description: "Technician's signature" },
];

const CustomerNotificationPage: React.FC = () => {
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [previewEmail, setPreviewEmail] = useState('');
  const [sendingPreview, setSendingPreview] = useState(false);

  const {
    data: EmailTemplateResponse,
    isLoading,
    error,
  } = useGet<EmailTemplateResponse>({
    url: '/home_services/email_template',
  });

  const { mutateAsync, isPending } = usePut();

  useEffect(() => {
    const result = EmailTemplateResponse?.data?.result;
    if (result) {
      setSubject(result.subject ?? '');
      setBody(result.body ?? '');
    }
  }, [EmailTemplateResponse]);

  const errorMessage = error ? 'Failed to load email template.' : '';
  const loading = isLoading || isPending;

  const handleSave = async () => {
    try {
      await mutateAsync({
        url: '/home_services/email_template',
        data: { email_template: { subject, body } },
      });
      showSuccessSnackBar?.('Email template saved.');
    } catch {
      showErrorSnackBar?.('Failed to save email template.');
    }
  };

  const handleSendPreview = async () => {
    if (!previewEmail) return;
    setSendingPreview(true);
    try {
      await axios.post('/home_services/email_template/preview', { email: previewEmail });
      showSuccessSnackBar?.(`Preview sent to ${previewEmail}`);
    } catch {
      showErrorSnackBar?.('Failed to send preview email.');
    } finally {
      setSendingPreview(false);
    }
  };

  const handleCopyVariable = (variable: string) => {
    navigator.clipboard.writeText(variable);
    showSuccessSnackBar?.(`Copied ${variable} to clipboard`);
  };

  const previewBody = body
    .replace(/\{\{customer_first_name\}\}/g, 'Jane')
    .replace(/\{\{customer_last_name\}\}/g, 'Doe')
    .replace(/\{\{technician_name\}\}/g, 'John Smith')
    .replace(/\{\{technician_signature\}\}/g, 'John Smith');

  return (
    <LoadingWrapper isLoading={loading} message="Loading...">
      <Grid container spacing={3} style={{ padding: '1% 2%' }}>
        <Grid item xs={12}>
          <PageHeader mainTitle="Home Services" subTitle="Customer Notification" />
        </Grid>

        {errorMessage && (
          <Grid item xs={12}>
            <ErrorMessage message={errorMessage} />
          </Grid>
        )}

        {/* Email Configuration — full width */}
        <Grid item xs={12}>
          <Paper style={{ padding: 24 }}>
            <Typography variant="h6" gutterBottom>
              Email Configuration
            </Typography>
            <Divider style={{ marginBottom: 16 }} />

            <TextField
              label="Email Subject"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={{ marginBottom: 24 }}
            />

            {/* Template Variables */}
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Template Variables — click to copy, then paste into the email body
              </Typography>
              {TEMPLATE_VARIABLES.map(({ variable, description }) => (
                <Chip
                  key={variable}
                  icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                  label={`${variable} — ${description}`}
                  onClick={() => handleCopyVariable(variable)}
                  clickable
                  variant="outlined"
                  size="small"
                  sx={{ mr: 1 }}
                />
              ))}
            </Box>

            <Typography variant="body2" color="textSecondary" gutterBottom>
              Email Body
            </Typography>
            <ReactQuill
              theme="snow"
              value={body}
              onChange={setBody}
              modules={QUILL_MODULES}
              style={{ height: 200, marginBottom: 48 }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save Template
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Send Preview */}
        <Grid item xs={12}>
          <Paper style={{ padding: 24 }}>
            <Typography variant="h6" gutterBottom>
              Send Preview
            </Typography>
            <Divider style={{ marginBottom: 16 }} />
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Send a preview of the last saved email template to any email address.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Email Address"
                value={previewEmail}
                onChange={(e) => setPreviewEmail(e.target.value)}
                size="small"
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSendPreview}
                disabled={sendingPreview || !previewEmail}
              >
                {sendingPreview ? 'Sending...' : 'Send Preview'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Email Preview — full width below */}
        <Grid item xs={12}>
          <Paper style={{ padding: 24 }}>
            <Typography variant="h6" gutterBottom>
              Email Preview
            </Typography>
            <Divider style={{ marginBottom: 16 }} />
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom style={{ fontSize: 16 }}>
              Subject: <strong>{subject || '(no subject)'}</strong>
            </Typography>
            <iframe
              key={previewBody ? 'filled' : 'empty'}
              srcDoc={buildPreviewHtml(previewBody)}
              sandbox=""
              title="Email preview"
              style={{ width: '100%', height: 460, border: '1px solid #e0e0e0', borderRadius: 4 }}
            />
          </Paper>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default CustomerNotificationPage;
